/**
 * BrandFormatterOutput — read-only preview of a generated Brand Formatter
 * result (Phase X.3.8). Renders the structured { title, blocks } in the Peace
 * palette so the operator sees roughly what the branded .docx will contain
 * before saving + downloading.
 *
 * Consecutive bullet / numbered blocks are grouped into a single <ul>/<ol> so
 * the preview list semantics match the document; a table block renders as a
 * real table (header row first) and the two signature kinds as the italic
 * name line + the small electronically-signed line. The actual letterhead +
 * Palatino styling live in the .docx; this is a faithful-enough on-screen
 * approximation, not a pixel render.
 */

import { type ReactNode } from 'react';
import type { BrandBlock } from '@/lib/api';

const HEADING_STYLE = { color: 'var(--color-sage-800)' } as const;
const SUBHEADING_STYLE = { color: 'var(--color-sage-400)' } as const;
const TITLE_STYLE = { color: 'var(--color-sage-800)', borderBottomColor: 'var(--color-warm-400)' } as const;

export interface BrandFormatterOutputProps {
  title: string;
  blocks: BrandBlock[];
}

export function BrandFormatterOutput({ title, blocks }: BrandFormatterOutputProps): ReactNode {
  // Group consecutive list blocks so bullets/numbered render as real lists.
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < blocks.length) {
    const b = blocks[i];
    if (b.kind === 'bullet' || b.kind === 'numbered') {
      const kind = b.kind;
      const items: string[] = [];
      while (i < blocks.length && blocks[i].kind === kind) {
        items.push(blocks[i].text);
        i += 1;
      }
      const listCls =
        kind === 'bullet'
          ? 'list-disc pl-6 space-y-1 text-sm text-ink'
          : 'list-decimal pl-6 space-y-1 text-sm text-ink';
      nodes.push(
        kind === 'bullet' ? (
          <ul key={`list-${i}`} className={listCls}>
            {items.map((t, j) => (
              <li key={j}>{t}</li>
            ))}
          </ul>
        ) : (
          <ol key={`list-${i}`} className={listCls}>
            {items.map((t, j) => (
              <li key={j}>{t}</li>
            ))}
          </ol>
        ),
      );
      continue;
    }
    if (b.kind === 'table') {
      const [head, ...body] = b.rows ?? [];
      nodes.push(
        <div key={i} className="overflow-x-auto">
          <table
            className="w-full border-collapse text-sm text-ink"
            data-testid="brand-formatter-output-table"
          >
            {head && (
              <thead>
                <tr>
                  {head.map((cell, c) => (
                    <th
                      key={c}
                      className="border border-sage-800/[12%] bg-sage-800 px-2 py-1 text-left font-semibold text-cream"
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border border-sage-800/[12%] px-2 py-1 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else if (b.kind === 'signature_name') {
      nodes.push(
        <p key={i} className="mt-4 text-base italic text-ink" data-testid="brand-formatter-output-signature-name">
          {b.text}
        </p>,
      );
    } else if (b.kind === 'signature_note') {
      nodes.push(
        <p key={i} className="text-xs italic text-ink-soft" data-testid="brand-formatter-output-signature-note">
          {b.text}
        </p>,
      );
    } else if (b.kind === 'heading') {
      nodes.push(
        <h3 key={i} className="text-lg font-semibold mt-2" style={HEADING_STYLE}>
          {b.text}
        </h3>,
      );
    } else if (b.kind === 'subheading') {
      nodes.push(
        <h4 key={i} className="text-base font-semibold" style={SUBHEADING_STYLE}>
          {b.text}
        </h4>,
      );
    } else {
      nodes.push(
        <p key={i} className="text-sm text-ink leading-relaxed">
          {b.text}
        </p>,
      );
    }
    i += 1;
  }

  return (
    <div className="space-y-2">
      <div
        className="bg-paper border border-sage-800/[8%] rounded-[12px] p-5 space-y-3"
        data-testid="brand-formatter-output"
      >
        <h2
          className="text-xl font-bold pb-2 border-b-2"
          style={TITLE_STYLE}
          data-testid="brand-formatter-output-title"
        >
          {title}
        </h2>
        {nodes}
      </div>

      {/* Editing happens in Word: the branded document keeps its formatting in
          the downloaded .docx, which is the deliverable. */}
      <p
        className="text-xs"
        style={{ color: 'var(--color-sage-300)' }}
        data-testid="edit-in-word-nudge"
      >
        To make edits, download and finalize in Word.
      </p>
    </div>
  );
}
